import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import * as Theme from '../../../images/icons/icons_theme';

const EcshgHnfCalender = ({ searchYear, searchMonth, searchURI, setCalender }) => {
    const now = new Date();
    const calendarRef = useRef(null);
    const containerRef = useRef(null);
    const [events, setEvents] = useState([]);

    const [params, setParams] = useState({
        searchYear: searchYear ?? now.getFullYear(),
        searchMonth:
            searchMonth?.padStart?.(2, '0') ??
            String(now.getMonth() + 1).padStart(2, '0'),
    });

    useEffect(() => {
        const fetchUserEvents = async () => {
            try {
                const response = await axios.get(searchURI, { params });
                const dataArray = response.data.ecshgHnfCalendar || [];
                console.log(dataArray);
                const mapped = dataArray.map((item, index) => {
                    const isResult = item.RSLT_SE_NM === '실적';
                    return {
                        id: index,
                        title: `${item.EMOGDESC || '기관명'} / ${item.NM} / ${item.SE_NM}`,
                        start: new Date(item.TR_EDC_DE?.split(' ~ ')[0]).toISOString().split('T')[0],
                        allDay: true,
                        extendedProps: {
                            SE_NM: item.SE_NM,
                            NM: item.NM,
                            EMOGDESC: item.EMOGDESC || '기관명',
                            TR_EDC_DE: item.TR_EDC_DE,
                            DC_CN: item.DC_CN,
                            EDC_PLACE: item.EDC_PLACE,
                            GNRL_RM: item.GNRL_RM || '',
                        },
                        backgroundColor: isResult ? '#33CCFF' : '#ff69cf',
                        color: "white",
                    };
                });
                setEvents(mapped);
                const calendarApi = calendarRef.current?.getApi();
                if (calendarApi) {
                    calendarApi.gotoDate(`${params.searchYear}-${params.searchMonth}-01`);
                }
            } catch (error) {
                console.error('일정 로딩 실패:', error);
            }
        };
        fetchUserEvents();
    }, [params, searchYear, searchMonth]);

    const handleMonthChange = (newYear, newMonth) => {
        const formattedMonth = String(newMonth).padStart(2, '0');
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            calendarApi.gotoDate(`${newYear}-${formattedMonth}-01`);
            setParams({
                searchYear: newYear,
                searchMonth: formattedMonth,
            });
        }
    };

    const updateDateState = () => {
        const calendarApi = calendarRef.current?.getApi();
        if (calendarApi) {
            const currentDate = calendarApi.getDate();
            setParams({
                searchYear: currentDate.getFullYear(),
                searchMonth: String(currentDate.getMonth() + 1).padStart(2, '0'),
            });
        }
    };

    const handlePrev = () => {
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.prev();
        updateDateState();
    };

    const handleNext = () => {
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.next();
        updateDateState();
    };

    const handleToday = () => {
        const calendarApi = calendarRef.current?.getApi();
        calendarApi?.today();
        updateDateState();
    };

    useEffect(() => {
        const observer = new ResizeObserver(() => {
            calendarRef.current?.getApi()?.updateSize();
        });
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        return () => observer.disconnect();
    }, []);

    return (
        <div className='mt-5 pb-20 px-6 relative w-full h-full overflow-auto'>
            <button className='search_btn bg_co_black absolute' onClick={() => setCalender(false)}>이전 화면</button>
            <h2 className='justify-self-center mb-4'>일정 공유</h2>
            <div className='flex items-center justify-center gap-2 mb-3'>
                <select
                    value={params.searchYear}
                    onChange={(e) => {
                        const newYear = Number(e.target.value);
                        setParams((prev) => ({
                            ...prev,
                            searchYear: newYear,
                        }));
                        handleMonthChange(newYear, params.searchMonth);
                    }}
                >
                    {Array.from({ length: 11 }, (_, i) => {
                        const year = now.getFullYear() - i;
                        return (
                            <option key={year} value={year}>
                                {year}년
                            </option>
                        );
                    })}
                </select>
                <button className='h_icon_box' onClick={handlePrev}>
                    <div className='icon'><Theme.PagePrev className='co_light' /></div>
                    <div className='hover_icon'><Theme.PagePrev className='tr_bright' /></div>
                </button>
                <select
                    value={params.searchMonth}
                    onChange={(e) => {
                        const newMonth = e.target.value; // 문자열 유지
                        setParams((prev) => ({
                            ...prev,
                            searchMonth: newMonth,
                        }));
                        handleMonthChange(params.searchYear, newMonth);
                    }}
                >
                    {Array.from({ length: 12 }, (_, i) => {
                        const monthStr = String(i + 1).padStart(2, '0');
                        return (
                            <option key={monthStr} value={monthStr}>
                                {monthStr}월
                            </option>
                        );
                    })}
                </select>
                <button className='h_icon_box' onClick={handleNext}>
                    <div className='icon'><Theme.PageNext className='co_light' /></div>
                    <div className='hover_icon'><Theme.PageNext className='tr_bright' /></div>
                </button>
                <button onClick={handleToday} className='search_btn bg_tr_primary'>Today</button>
            </div>

            <div ref={containerRef} className='w-full'>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    initialDate={`${params.searchYear}-${params.searchMonth}-01`}
                    events={events}
                    height="auto"
                    headerToolbar={false}
                    fixedWeekCount={true}
                    showNonCurrentDates={false}
                    eventMouseEnter={(info) => {
                        const { SE_NM, NM, TR_EDC_DE, EDC_PLACE, DC_CN, GNRL_RM, EMOGDESC } = info.event.extendedProps;
                        const tooltipContent = `
                    <strong>구분:</strong> ${SE_NM}<br/>
                    <strong>참석자:</strong> ${NM}<br/>
                    <strong>기관:</strong> ${EMOGDESC}<br/>
                    <strong>내용:</strong> ${DC_CN}<br/>
                    <strong>교육일:</strong> ${TR_EDC_DE}<br/>
                    <strong>비고:</strong> ${GNRL_RM}<br/>
                    <strong>장소:</strong> ${EDC_PLACE}<br/>
                    `;
                        const tooltip = document.createElement('div');
                        tooltip.innerHTML = tooltipContent;
                        tooltip.style.position = 'absolute';
                        tooltip.style.zIndex = 1000;
                        tooltip.style.background = 'rgba(0,0,0,0.75)';
                        tooltip.style.color = '#fff';
                        tooltip.style.padding = '4px 8px';
                        tooltip.style.borderRadius = '4px';
                        tooltip.style.fontSize = '12px';
                        tooltip.style.pointerEvents = 'none';

                        document.body.appendChild(tooltip);

                        const moveTooltip = (e) => {
                            tooltip.style.left = `${e.pageX + 10}px`;
                            tooltip.style.top = `${e.pageY + 10}px`;
                        };

                        document.addEventListener('mousemove', moveTooltip);

                        info.el.addEventListener('mouseleave', () => {
                            document.body.removeChild(tooltip);
                            document.removeEventListener('mousemove', moveTooltip);
                        }, { once: true });
                    }}
                />
            </div>
        </div>
    );
};

export default EcshgHnfCalender;